// src/modules/purityAnalyzer/pages/PurityDashboard.jsx
// Laboratory dashboard: today's tests, purity stats, calibration health,
// machine accuracy, pending XRF, recent tests. HO gets a branch filter.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Row, Col, Card, Typography, Tag, Table, Space, Select, Button, Empty, Tooltip,
} from 'antd';
import {
  DashboardOutlined, ExperimentOutlined, GoldOutlined, SafetyCertificateOutlined,
  FireOutlined, RiseOutlined, FallOutlined, ClockCircleOutlined, PlusOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { fetchDashboard } from '../api';
import useBranchScope from '../hooks/useBranchScope';
import MetricCard from '../components/MetricCard';
import KaratBadge from '../components/KaratBadge';
import {
  purityColor, QUALITY_COLORS, FLUID_CONDITION_META, XRF_STATUS_COLORS,
} from '../constants/purityConstants';

const { Title, Text } = Typography;

export default function PurityDashboard() {
  const navigate = useNavigate();
  const { isHO, branches } = useBranchScope();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchId, setBranchId] = useState();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchDashboard({ branchId });
      setData(res?.data?.data || null);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const today = data?.today || {};
  const overall = data?.overall || {};
  const accuracy = data?.machineAccuracy || {};

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          <DashboardOutlined style={{ marginRight: 10 }} />Purity Analyzer
        </Title>
        <Space wrap>
          {isHO && (
            <Select allowClear placeholder="All branches" style={{ width: 180 }}
              options={branches.map((b) => ({ value: b._id, label: b.name }))}
              onChange={setBranchId} />
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/purity/run')}>
            Run Test
          </Button>
        </Space>
      </Space>

      {/* ── KPI row ── */}
      <Row gutter={[12, 12]}>
        <Col xs={12} sm={8} md={6} lg={4}>
          <MetricCard icon={<ExperimentOutlined />} title="Today's Tests"
            value={today.count ?? 0} precision={0} size="large" delay={0.02} />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <MetricCard icon={<GoldOutlined />} title="Avg Purity (today)"
            value={today.avgPurity} suffix="%" precision={2}
            color={today.avgPurity ? purityColor(today.avgPurity) : undefined} delay={0.06} />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <MetricCard icon={<RiseOutlined />} title="Highest Purity"
            value={overall.maxPurity} suffix="%" precision={2} color="#52c41a" delay={0.1} />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <MetricCard icon={<FallOutlined />} title="Lowest Purity"
            value={overall.minPurity} suffix="%" precision={2} color="#fa8c16" delay={0.14} />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <MetricCard icon={<GoldOutlined />} title="Avg Density"
            value={overall.avgDensity} suffix="g/cm³" precision={3} color="#d4a017" delay={0.18} />
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <MetricCard icon={<ClockCircleOutlined />} title="Pending XRF"
            value={data?.pendingXrf ?? 0} precision={0}
            color={data?.pendingXrf ? '#faad14' : undefined} delay={0.22}
            hint={data?.pendingXrf ? 'awaiting verification' : 'all verified'} />
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        {/* ── Calibration health ── */}
        <Col xs={24} lg={10}>
          <Card
            size="small" loading={loading}
            title={<span><FireOutlined style={{ marginRight: 8 }} />Calibration Health</span>}
            extra={<a onClick={() => navigate('/purity/calibrations')}>History</a>}
            style={{ height: '100%' }}
          >
            {(data?.activeCalibrations || []).length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No ACTIVE calibration — run a copper calibration" />
            ) : (
              (data.activeCalibrations || []).map((cal) => {
                const fluid = FLUID_CONDITION_META[cal.fluidCondition] || FLUID_CONDITION_META.STABLE;
                const ageDays = dayjs().diff(dayjs(cal.activatedAt || cal.createdAt), 'day');
                return (
                  <Card key={cal._id} size="small" style={{ marginBottom: 8 }}
                    styles={{ body: { padding: '8px 12px' } }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                      <Space wrap>
                        <Text strong>{cal.calibrationNumber}</Text>
                        {cal.machine && <Tag color="purple">{cal.machine.machineCode}</Tag>}
                        {isHO && cal.branch && <Tag>{cal.branch.name}</Tag>}
                        <Tag color={fluid.color}>{fluid.label}</Tag>
                      </Space>
                      <Space wrap>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ρ {cal.dieselDensity} g/cm³
                        </Text>
                        <Tooltip title="Days since activation">
                          <Tag color={ageDays > 7 ? 'orange' : 'green'}>{ageDays}d old</Tag>
                        </Tooltip>
                      </Space>
                    </Space>
                  </Card>
                );
              })
            )}
            {/* ── Machine accuracy ── */}
            <Card size="small" style={{ marginTop: 8 }}
              title={<span><SafetyCertificateOutlined style={{ marginRight: 8 }} />Prediction Accuracy (vs XRF)</span>}>
              {accuracy.count ? (
                <Space size={20} wrap>
                  <Text>Samples <Text strong>{accuracy.count}</Text></Text>
                  <Text>RMSE <Text strong>{accuracy.rmse}%</Text></Text>
                  <Text>Avg Error <Text strong>{accuracy.avgAbsError}%</Text></Text>
                  <Tag color={QUALITY_COLORS[accuracy.quality] || 'default'}>{accuracy.quality}</Tag>
                </Space>
              ) : (
                <Text type="secondary">No XRF-verified samples yet.</Text>
              )}
            </Card>
          </Card>
        </Col>

        {/* ── Recent tests ── */}
        <Col xs={24} lg={14}>
          <Card
            size="small" loading={loading}
            title={<span><ExperimentOutlined style={{ marginRight: 8 }} />Recent Tests</span>}
            extra={<a onClick={() => navigate('/purity/tests')}>All tests</a>}
            style={{ height: '100%' }}
          >
            <Table
              rowKey="_id" size="small" pagination={false}
              dataSource={data?.recentTests || []}
              onRow={(r) => ({ onClick: () => navigate(`/purity/tests/${r._id}`), style: { cursor: 'pointer' } })}
              columns={[
                { title: 'Test #', dataIndex: 'testNumber', width: 100 },
                {
                  title: 'Purity', dataIndex: 'predictedPurity', width: 90,
                  render: (v) => <Text strong style={{ color: purityColor(v) }}>{v?.toFixed(2)}%</Text>,
                },
                {
                  title: 'Karat', dataIndex: 'estimatedKarat', width: 80,
                  render: (v) => <KaratBadge karat={v} />,
                },
                { title: 'Customer', dataIndex: ['customer', 'name'], render: (v) => v || '—' },
                ...(isHO ? [{ title: 'Branch', dataIndex: ['branch', 'name'], width: 110 }] : []),
                {
                  title: 'XRF', dataIndex: 'xrfStatus', width: 90,
                  render: (v) => <Tag color={XRF_STATUS_COLORS[v]}>{v}</Tag>,
                },
                {
                  title: 'When', dataIndex: 'createdAt', width: 120,
                  render: (v) => dayjs(v).format('DD MMM · HH:mm'),
                },
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
